import { BrowserRouter, Routes, Route } from 'react-router';
import { createContext, useReducer, useRef, useEffect, useState } from 'react';

import Layout from './components/Layout';
import Alert from './components/alert/Alert';
import Dashboard from './components/dashboard/Dashboard';
import ChartPage from './components/charts/ChartPage';
import ProtectedRoute from './ProtectedRoute';
import Login from './components/login/Login';
import ActivityDialog from './components/ActivityDialog';
import { addCalculatedColumns } from './components/custom';
import { getServerURL } from './utils';
import {
  LOGIN,
  LOGOUT,
  SET_PAGE_TITLE,
  SET_ALERT_DATA,
  UPDATE_ALERT_STATUS,
  UPDATE_DISCHARGE_DATE,
  CHANGE_APPOINTMENT_DATE,
  UPDATE_ALERT_STATUS_AND_APPOINTMENT,
  SET_CHART_DATA,
  SET_CHART_DATA_FETCHED,
  UPDATE_ACCESS_TOKEN,
} from './dispatchActionTypes.js';

const SERVER_URL = getServerURL();
const MOUSEMOVE = 'mousemove';
const ACTIVITY_TIMEOUT = 1;
const REEVALUATE_TIMEOUT = 28;

const minutesToMilliseconds = (minutes) => {
  return minutes * 60 * 1000;
};

const initialState = {
  alertData: [],
  headerTitle: '',
  user: {
    name: {
      first: null,
      last: null,
    },
    email: null,
    phoneNumber: null,
    userType: null,
    organization: null,
    chatID: null,
    isAuthenticated: false,
  },
  chartData: {
    isFetched: false,
    content: [],
  },
  token: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case LOGIN: {
      return {
        ...state,
        user: action.user,
        alertData: action.alertData,
        token: action.token,
      };
    }
    case LOGOUT: {
      // Want to keep headerTitle
      return {
        ...state,
        user: initialState.user,
        alertData: initialState.alertData,
        chartData: initialState.chartData,
        token: initialState.token,
      };
    }
    case SET_PAGE_TITLE: {
      return {
        ...state,
        headerTitle: action.title,
      };
    }
    case SET_ALERT_DATA: {
      const { newAlert } = action.payload;
      return {
        ...state,
        alertData: [newAlert, ...state.alertData],
      };
    }
    case UPDATE_ALERT_STATUS: {
      const {
        alertID,
        historyEntry,
        nextAlertStatus,
        shouldRemoveFromDashboard = null,
        unclaimedClinics,
        // Tree only set by socket
        // unclaim status updates.
        isTreeOnDashboard = true,
      } = action.payload;
      const updatedAlertData = state.alertData.map((entry) => {
        if (entry.alertID === alertID) {
          if (shouldRemoveFromDashboard) return null;
          const updatedData = {
            ...entry,
            alertStatus: nextAlertStatus,
            actionHistory: {
              actions: [...entry.actionHistory.actions, historyEntry],
            },
            unclaimedClinics,
            // Leave tree as is unless explicitly
            // passed as false.
            isTreeOnDashboard: !isTreeOnDashboard
              ? false
              : entry.isTreeOnDashboard,
          };
          const withCCs = addCalculatedColumns(
            updatedData,
            state.user.organization,
          );
          return withCCs;
        }
        return entry;
      });
      return {
        ...state,
        alertData: shouldRemoveFromDashboard
          ? updatedAlertData.filter((entry) => entry !== null)
          : updatedAlertData,
      };
    }
    case UPDATE_DISCHARGE_DATE: {
      const { alertID, historyEntry, updatedDischargeDate } = action.payload;
      return {
        ...state,
        alertData: state.alertData.map((entry) => {
          if (entry.alertID === alertID) {
            const updatedData = {
              ...entry,
              dischargeDate: updatedDischargeDate,
              actionHistory: {
                actions: [...entry.actionHistory.actions, historyEntry],
              },
            };
            const withCCs = addCalculatedColumns(
              updatedData,
              state.user.organization,
            );
            return withCCs;
          }
          return entry;
        }),
      };
    }
    case CHANGE_APPOINTMENT_DATE: {
      const { alertID, historyEntry, changedAppointmentDate } = action.payload;
      return {
        ...state,
        alertData: state.alertData.map((entry) => {
          if (entry.alertID === alertID) {
            const updatedData = {
              ...entry,
              appointmentDate: changedAppointmentDate,
              actionHistory: {
                actions: [...entry.actionHistory.actions, historyEntry],
              },
            };
            const withCCs = addCalculatedColumns(
              updatedData,
              state.user.organization,
            );
            return withCCs;
          }
          return entry;
        }),
      };
    }
    case UPDATE_ALERT_STATUS_AND_APPOINTMENT: {
      const {
        alertID,
        historyEntry,
        alertStatus,
        appointmentDate,
        historyEntryAppointmentDate,
      } = action.payload;
      return {
        ...state,
        alertData: state.alertData.map((entry) => {
          if (entry.alertID === alertID) {
            const updatedData = {
              ...entry,
              appointmentDate,
              alertStatus,
              actionHistory: {
                actions: [
                  ...entry.actionHistory.actions,
                  historyEntry,
                  historyEntryAppointmentDate,
                ],
              },
            };
            const withCCs = addCalculatedColumns(
              updatedData,
              state.user.organization,
            );
            return withCCs;
          }
          return entry;
        }),
      };
    }
    case SET_CHART_DATA: {
      return {
        ...state,
        chartData: {
          isFetched: true,
          content: action.payload.chartData,
        },
      };
    }
    case SET_CHART_DATA_FETCHED: {
      return {
        ...state,
        chartData: {
          ...state.chartData,
          isFetched: true,
        },
      };
    }
    case UPDATE_ACCESS_TOKEN: {
      return {
        ...state,
        token: action.payload.accessToken,
      };
    }
    default: {
      throw new Error(
        `State reducer function called with an unknown type: ${action.type}`,
      );
    }
  }
};

export const ClinicRxContext = createContext(null);

export default function App() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const lastActivityRef = useRef(null);
  const timeoutIdRef = useRef(null);
  const [isActivityDialogOpen, setIsActivityDialogOpen] = useState(false);

  const updateAccessToken = (token) => {
    dispatch({
      type: UPDATE_ACCESS_TOKEN,
      payload: { accessToken: token },
    });
  };

  const handleLogout = () => {
    dispatch({ type: LOGOUT });
    fetch(`${SERVER_URL}/logout`, {
      credentials: 'include',
    }).catch((err) => console.error('Error making request to /logout:', err));
  };

  const autoAttemptRefresh = async () => {
    const refreshResult = await fetch(`${SERVER_URL}/refresh-token`, {
      credentials: 'include',
    });
    if (!refreshResult.ok) {
      // Open the dialog on error
      // to try again.
      // If that fails, the activity
      // dialog component has a
      // something went wrong
      // snackbar.
      setIsActivityDialogOpen(true);
      return;
    }
    const { token } = await refreshResult.json();
    updateAccessToken(token);
    activityCheck();
  };

  const activityCheck = () => {
    timeoutIdRef.current = setTimeout(() => {
      if (
        !lastActivityRef.current ||
        Date.now() - lastActivityRef.current >
          minutesToMilliseconds(ACTIVITY_TIMEOUT)
      ) {
        setIsActivityDialogOpen(true);
      } else {
        autoAttemptRefresh();
      }
    }, minutesToMilliseconds(REEVALUATE_TIMEOUT));
  };

  useEffect(() => {
    const trackActivity = () => {
      lastActivityRef.current = Date.now();
    };
    document.addEventListener(MOUSEMOVE, trackActivity);
    return () => {
      document.removeEventListener(MOUSEMOVE, trackActivity);
      clearTimeout(timeoutIdRef.current);
    };
  }, []);

  const fetchProtected = async (fetchFn) => {
    // No try / catch because
    // callers handle throws.
    const response = await fetchFn(`Bearer ${state.token}`);
    if (response.status !== 401) {
      return response;
    }
    const refreshResult = await fetch(`${SERVER_URL}/refresh-token`, {
      credentials: 'include',
    });
    if (!refreshResult.ok) {
      handleLogout();
      return response;
    }
    const { token } = await refreshResult.json();
    updateAccessToken(token);
    activityCheck();
    return await fetchFn(`Bearer ${token}`);
  };

  return (
    <ClinicRxContext.Provider value={{ state, dispatch, fetchProtected }}>
      <>
        <BrowserRouter>
          <Routes>
            <Route element={<Layout />}>
              <Route
                path='/'
                element={<Login checkActivity={activityCheck} />}
              />
              <Route element={<ProtectedRoute />}>
                <Route
                  path='/alert'
                  element={<Alert />}
                />
                <Route
                  path='/dashboard'
                  element={<Dashboard />}
                />
                <Route
                  path='/charts'
                  element={<ChartPage />}
                />
              </Route>
            </Route>
          </Routes>
        </BrowserRouter>
        {isActivityDialogOpen && (
          <ActivityDialog
            isOpen={isActivityDialogOpen}
            handleClose={() => setIsActivityDialogOpen(false)}
            checkActivity={activityCheck}
          />
        )}
      </>
    </ClinicRxContext.Provider>
  );
}
